/**
 * CsvUpload — admin self-serve panel for two flows:
 *   1. Country card patch  (CSV: country_name,field,value)
 *   2. Master partnerships replace (CSV: 17-column master format)
 *
 * Renders a structured receipt after upload — every row's before/after values,
 * skipped rows with reasons, per-bschool deltas — so the admin sees exactly
 * what changed without checking logs.
 */
import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Upload, CheckCircle2, AlertTriangle, FileDown } from "lucide-react";

const FLOWS = {
  country_card_patch: {
    label: "Country card patch",
    hint: "3-column CSV. One row per (country, field, value) pair. Whitelisted fields only.",
    endpoint: "/admin/csv/country-card-patch",
    example: "country_name,field,value\nPoland,fee_stat,EUR 200\nPoland,funds_stat,PLN 1010/mo",
  },
  partnerships_replace: {
    label: "Master partnerships replace",
    hint: "Full master CSV. 17-column format with home_institution + partner_institution. Wipes and rebuilds partnerships + exchange_partners.",
    endpoint: "/admin/csv/partnerships-replace",
    example: null,
  },
};

export default function CsvUpload() {
  const [flow, setFlow] = useState("country_card_patch");
  const [schema, setSchema] = useState(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    api.get("/admin/csv/schema").then((r) => setSchema(r.data)).catch(() => {});
  }, []);

  const submit = async () => {
    if (!file) { toast.error("Pick a .csv file first"); return; }
    setBusy(true);
    setReceipt(null);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post(FLOWS[flow].endpoint, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setReceipt(r.data);
      toast.success("CSV applied");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Upload failed");
      setReceipt({ error: e?.response?.data?.detail || String(e) });
    } finally {
      setBusy(false);
    }
  };

  const downloadExample = () => {
    const text = FLOWS[flow].example;
    if (!text) return;
    const blob = new Blob([text], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${flow}-example.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="mt-6" data-testid="admin-csv-panel">
      {/* Flow chooser */}
      <div className="flex gap-2 flex-wrap">
        {Object.entries(FLOWS).map(([k, v]) => (
          <button
            key={k}
            onClick={() => { setFlow(k); setReceipt(null); setFile(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}
            data-testid={`csv-flow-${k}`}
            className={"px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest border transition-colors " +
              (flow === k ? "bg-ink text-white border-ink" : "text-ink border-ink/20 hover:border-ink")}
          >
            {v.label}
          </button>
        ))}
      </div>

      <div className="mt-4 text-sm text-muted max-w-2xl">{FLOWS[flow].hint}</div>

      {/* Whitelisted fields for country_card_patch */}
      {flow === "country_card_patch" && schema?.country_card_patch && (
        <div className="mt-4 rounded-xl border border-ink/10 p-4 bg-tint/40" data-testid="csv-whitelist">
          <div className="text-[11px] uppercase tracking-widest text-soft font-bold">Whitelisted fields</div>
          <div className="mt-2 flex gap-1.5 flex-wrap">
            {schema.country_card_patch.whitelisted_fields.map((f) => (
              <code key={f} className="text-[11px] px-1.5 py-0.5 bg-white border border-ink/10 rounded">{f}</code>
            ))}
          </div>
        </div>
      )}

      {/* Canonical home institutions for partnerships_replace */}
      {flow === "partnerships_replace" && schema?.partnerships_replace && (
        <div className="mt-4 rounded-xl border border-ink/10 p-4 bg-tint/40" data-testid="csv-canonical-homes">
          <div className="text-[11px] uppercase tracking-widest text-soft font-bold">Canonical home_institution values ({schema.partnerships_replace.canonical_homes.length})</div>
          <ul className="mt-2 grid md:grid-cols-2 gap-x-6 gap-y-1 text-xs text-muted">
            {schema.partnerships_replace.canonical_homes.map((h) => (
              <li key={h} className="truncate">· {h}</li>
            ))}
          </ul>
        </div>
      )}

      {/* File picker */}
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <label
          data-testid="csv-file-label"
          className="moe-btn moe-btn-primary cursor-pointer"
        >
          <Upload size={14}/> Choose CSV
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            data-testid="csv-file-input"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="hidden"
          />
        </label>
        {file && <div className="text-sm text-muted" data-testid="csv-file-name">{file.name} · {(file.size/1024).toFixed(1)} KB</div>}
        <button
          data-testid="csv-submit"
          disabled={!file || busy}
          onClick={submit}
          className="moe-btn moe-btn-ghost disabled:opacity-40"
        >
          {busy ? "Applying…" : "Apply CSV"}
        </button>
        {FLOWS[flow].example && (
          <button onClick={downloadExample} data-testid="csv-example" className="moe-btn moe-btn-ghost text-xs">
            <FileDown size={13}/> Example CSV
          </button>
        )}
      </div>

      {/* Receipt */}
      {receipt && (
        <div className="mt-6 rounded-xl border border-ink/12 bg-white p-5" data-testid="csv-receipt">
          {receipt.error ? (
            <div className="flex items-start gap-2 text-[color:var(--moe-pink)]" data-testid="csv-receipt-error">
              <AlertTriangle size={16} className="shrink-0 mt-0.5"/>
              <div className="text-sm font-semibold">{receipt.error}</div>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 text-ink font-bold">
                <CheckCircle2 size={16} className="text-elec"/> CSV applied
              </div>
              <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                {Object.entries(receipt)
                  .filter(([, v]) => typeof v === "number")
                  .map(([k, v]) => (
                    <div key={k} className="rounded-lg bg-tint p-3">
                      <div className="text-[10px] uppercase tracking-widest text-soft font-bold">{k.replace(/_/g, " ")}</div>
                      <div className="mt-1 text-xl font-bold tabular-nums">{v}</div>
                    </div>
                  ))}
              </div>
              {/* per-row applied */}
              {Array.isArray(receipt.applied) && receipt.applied.length > 0 && (
                <div className="mt-4">
                  <div className="text-[11px] uppercase tracking-widest text-soft font-bold">Applied</div>
                  <ul className="mt-2 text-xs text-ink space-y-1" data-testid="csv-receipt-applied">
                    {receipt.applied.map((r, i) => (
                      <li key={i} className="font-mono">
                        line {r.line} · {r.country} · {r.field}: <span className="text-muted line-through">{String(r.before ?? "–")}</span> → <span className="text-elec">{String(r.after)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {/* skipped */}
              {Array.isArray(receipt.skipped) && receipt.skipped.length > 0 && (
                <div className="mt-4">
                  <div className="text-[11px] uppercase tracking-widest text-[color:var(--moe-pink)] font-bold">Skipped</div>
                  <ul className="mt-2 text-xs text-ink space-y-1" data-testid="csv-receipt-skipped">
                    {receipt.skipped.map((r, i) => (
                      <li key={i} className="font-mono">line {r.line}. {r.reason}</li>
                    ))}
                  </ul>
                </div>
              )}
              {/* per-bschool deltas */}
              {Array.isArray(receipt.per_bschool_deltas) && receipt.per_bschool_deltas.length > 0 && (
                <div className="mt-4">
                  <div className="text-[11px] uppercase tracking-widest text-soft font-bold">Per B-school delta</div>
                  <ul className="mt-2 text-xs text-ink space-y-1" data-testid="csv-receipt-deltas">
                    {receipt.per_bschool_deltas.map((d, i) => (
                      <li key={i} className="font-mono">
                        {d.bschool_short}: {d.before} → {d.after} <span className={d.delta > 0 ? "text-elec" : "text-[color:var(--moe-pink)]"}>({d.delta > 0 ? "+" : ""}{d.delta})</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {/* newly populated */}
              {Array.isArray(receipt.newly_populated) && receipt.newly_populated.length > 0 && (
                <div className="mt-4">
                  <div className="text-[11px] uppercase tracking-widest text-elec font-bold">★ Newly populated</div>
                  <ul className="mt-2 text-xs text-ink space-y-1" data-testid="csv-receipt-newly-populated">
                    {receipt.newly_populated.map((n, i) => (
                      <li key={i} className="font-mono">{n.bschool_short} → {n.count} host universities</li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
